// pages/BuildPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import BuildLogs from '../components/BuildLogs';
import BuildsTabs from '../components/BuildsTabs';
import LifecycleShell from '../components/LifecycleShell';
import SearchableFlavorSelect from '../components/SearchableFlavorSelect';
import { ArrowClockwise, CheckCircle, GitBranch, WarningCircle } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { toast, Toaster } from 'react-hot-toast';
import Config from "../config/Config";
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchJson, getFriendlyErrorMessage } from '../utils/api';
import { useBuildLog } from '../contexts/BuildLogContext';

const baseUrl = Config.ApiBaseUrl;

// The backend reads signing details from the flavor file; the browser only names the flavor and version.
export const createBuildPayload = (flavor, versionCode, versionName, customAppId) => ({
    flavorName: flavor.flavorName,
    versionCode: parseInt(versionCode, 10),
    versionName: String(versionName).trim(),
    ...(customAppId ? { customAppId } : {}),
});

// Keeps which flavor and which custom app a page is about when moving between /builder URLs.
const builderQuery = (flavorName, customAppId) => {
    const query = new URLSearchParams();
    if (flavorName) query.set('flavor', flavorName);
    if (customAppId) query.set('customApp', customAppId);
    const text = query.toString();
    return text ? `?${text}` : '';
};

export const resolveRequestedFlavor = (options, requestedFlavor) => (
    requestedFlavor ? options.find((item) => item.value === requestedFlavor) : undefined
);

function BuildPage() {
    const { language } = useLanguage();
    const t = translations[language];
    const navigate = useNavigate();
    const [sessionInput, setSessionInput] = useState('');
    const [versionCode, setVersionCode] = useState('');
    const [versionName, setVersionName] = useState('');
    const [selectedFlavor, setSelectedFlavor] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [activeBuildId, setActiveBuildId] = useState('');
    const { session_id } = useParams();
    const [searchParams] = useSearchParams();
    const requestedFlavor = searchParams.get('flavor');
    const customAppId = searchParams.get('customApp');
    // The log stream lives in the app-level provider so it keeps running when this page is left.
    const stream = useBuildLog();
    const { follow, release, refreshActive } = stream;
    const logs = session_id ? stream.logs : '';
    const logsError = session_id ? stream.error : '';
    const buildStatus = session_id ? stream.status : 'idle';
    const [options, setOptions] = useState([]);
    const [isLoadingFlavors, setIsLoadingFlavors] = useState(false);
    const [flavorError, setFlavorError] = useState('');
    const [syncStatus, setSyncStatus] = useState('idle');
    const [syncSessionId, setSyncSessionId] = useState('');
    const [syncResult, setSyncResult] = useState(null);
    const [syncError, setSyncError] = useState('');
    const [syncLogs, setSyncLogs] = useState([]);
    const buildButtonDisabled = submitting || buildStatus === 'running';
    const buildFormInvalid = !selectedFlavor || !Number.isInteger(Number(versionCode)) || Number(versionCode) <= 0 || !String(versionName).trim();
    const getErrorMessage = useCallback((error, fallback) => getFriendlyErrorMessage(error, {
        auth: t.noBuildAccess,
        conflict: t.requestConflict,
        network: t.backendUnavailable,
        server: t.serverError,
        generic: fallback,
    }), [t.backendUnavailable, t.noBuildAccess, t.requestConflict, t.serverError]);

    const handleSubmit = async () => {
        if (!selectedFlavor) {
            toast.error(t.selectFlavorFirst);
            return;
        }

        setSubmitting(true);
        setActiveBuildId('');
        const loadingToast = toast.loading(t.sendingData);
        try {
            const data = await fetchJson(`${baseUrl}build-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(createBuildPayload(selectedFlavor, versionCode, versionName, customAppId)),
            });

            toast.dismiss(loadingToast);
            toast.success(t.dataSentSuccessfully);
            navigate(`/builder/${encodeURIComponent(data.session_id)}${builderQuery(selectedFlavor.flavorName, customAppId)}`);
        } catch (error) {
            toast.dismiss(loadingToast);
            if (error.code === 'build_in_progress') {
                setActiveBuildId(error.payload?.active_session_id || '');
                toast.error(t.buildAlreadyRunning);
            } else if (error.code === 'sync_in_progress') {
                toast.error(t.buildBlockedBySync);
            } else if (error.code === 'invalid_flavor') {
                toast.error(error.message);
            } else {
                toast.error(getErrorMessage(error, t.errorSendingData));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const leaveSession = () => {
        navigate(`/builder${builderQuery(selectedFlavor?.flavorName, customAppId)}`);
    };

    const openSession = () => {
        const id = sessionInput.trim();
        if (id) navigate(`/builder/${encodeURIComponent(id)}`);
    };

    const handleVersionCodeChange = (e) => {
        setVersionCode(e.target.value);
    };

    const handleVersionNameChange = (e) => {
        setVersionName(e.target.value);
    };

    const startRepositorySync = async () => {
        setSyncStatus('queued');
        setSyncResult(null);
        setSyncError('');
        setSyncLogs([]);
        try {
            const data = await fetchJson(`${baseUrl}repository/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!data.session_id) throw new Error('Repository sync session missing');
            setSyncSessionId(data.session_id);
        } catch (error) {
            setSyncStatus('failed');
            setSyncError(error.code === 'build_in_progress' ? t.syncBlockedByBuild : getErrorMessage(error, t.syncStartFailed));
        }
    };

    useEffect(() => {
        if (!syncSessionId) return undefined;
        let cancelled = false;
        let timer;

        const poll = async () => {
            try {
                const data = await fetchJson(`${baseUrl}repository/sync/${encodeURIComponent(syncSessionId)}`);
                if (cancelled) return;
                setSyncStatus(data.status || 'failed');
                setSyncLogs(Array.isArray(data.logs) ? data.logs : []);
                if (data.status === 'success') {
                    setSyncResult(data.result || null);
                    return;
                }
                if (data.status === 'failed') {
                    setSyncError(typeof data.error === 'string' ? data.error : data.error?.message || t.syncFailed);
                    return;
                }
                timer = setTimeout(poll, 2000);
            } catch (error) {
                if (!cancelled) {
                    setSyncStatus('failed');
                    setSyncError(getErrorMessage(error, t.syncStatusFailed));
                }
            }
        };

        poll();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [getErrorMessage, syncSessionId, t.syncFailed, t.syncStatusFailed]);

    useEffect(() => {
        if (!session_id) return undefined;
        follow(session_id, requestedFlavor || '');
        return release;
    }, [session_id, requestedFlavor, follow, release]);

    // The build keeps running on the server when the user leaves. Coming back to /builder reopens
    // the one that is running so its log is on screen instead of an empty form.
    useEffect(() => {
        if (session_id) return undefined;
        let cancelled = false;
        Promise.resolve(refreshActive()).then((data) => {
            const active = data?.active;
            if (cancelled || !active?.session_id) return;
            navigate(`/builder/${encodeURIComponent(active.session_id)}${builderQuery(active.flavorName, active.customAppId)}`, { replace: true });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [navigate, refreshActive, session_id]);

    const loadData = useCallback(async () => {
        setIsLoadingFlavors(true);
        setFlavorError('');
        try {
            const data = await fetchJson(`${baseUrl}flavors`);
            const flavorOptions = data.map((flavor) => ({
                value: flavor.flavorName,
                label: `${flavor.applicationName} (${flavor.flavorName})`,
                data: flavor
            }));
            setOptions(flavorOptions);
            if (requestedFlavor) {
                const match = resolveRequestedFlavor(flavorOptions, requestedFlavor);
                if (match) {
                    setSelectedFlavor(match.data);
                    setVersionCode(match.data.versionCode || '');
                    setVersionName(match.data.versionName || '');
                } else {
                    setSelectedFlavor(null);
                    setFlavorError(t.invalidFlavorQuery);
                }
            }
        } catch (error) {
            setFlavorError(getErrorMessage(error, t.loadFlavorsFailed));
        } finally {
            setIsLoadingFlavors(false);
        }
    }, [getErrorMessage, requestedFlavor, t.invalidFlavorQuery, t.loadFlavorsFailed]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const phaseStates = [
        buildStatus === 'idle' ? 'current' : 'done',
        buildStatus === 'running' ? 'current' : (buildStatus === 'success' || buildStatus === 'failed') ? 'done' : 'todo',
        buildStatus === 'success' ? 'done' : buildStatus === 'failed' ? 'blocked' : 'todo',
    ];
    const syncRunning = syncStatus === 'queued' || syncStatus === 'running';
    const syncChip = syncStatus === 'success' ? 'chip--ok' : syncStatus === 'failed' ? 'chip--danger' : syncRunning ? 'chip--warn' : '';
    const buildChip = buildStatus === 'running' ? 'chip--warn' : buildStatus === 'success' ? 'chip--ok' : buildStatus === 'failed' ? 'chip--danger' : '';

    return (
        <LifecycleShell title={t.buildLogs} eyebrow={t.buildWorkspace} lead={t.buildLogsSub} action={<BuildsTabs />}>
            {customAppId && <p className="callout">{t.buildForCustomApp} <Link to={`/custom-apps/${encodeURIComponent(customAppId)}`}>{t.openCustomApp}</Link></p>}

            <ol className="stepper stepper--three" aria-label={t.buildWorkspace}>
                {[t.configureStep, t.processStep, t.resultStep].map((label, index) => (
                    <li key={label} className={`step is-${phaseStates[index]}`} aria-current={phaseStates[index] === 'current' ? 'step' : undefined}>
                        <span className="step__dot" aria-hidden="true"><span className="step__num">{index + 1}</span></span>
                        <span className="step__label">{label}</span>
                    </li>
                ))}
            </ol>

            <div className="split split--even">
                <div className="stack">
                    <section className="card stack" aria-labelledby="repository-sync-heading">
                        <div>
                            <p className="page-head__eyebrow"><GitBranch size={16} aria-hidden="true" /> {t.repositorySync}</p>
                            <h2 id="repository-sync-heading">{t.updateAssets}</h2>
                            <p className="card__sub">{t.updateAssetsDescription}</p>
                        </div>
                        <div className="cluster">
                            <button className="btn" type="button" disabled={syncRunning} onClick={startRepositorySync}>
                                <ArrowClockwise size={18} aria-hidden="true" /> {syncRunning ? t.updatingAssets : t.updateAssets}
                            </button>
                            <span className={`chip ${syncChip}`}>
                                {syncStatus === 'queued' ? t.syncQueued : syncStatus === 'running' ? t.syncRunning : syncStatus === 'success' ? t.syncSuccess : syncStatus === 'failed' ? t.syncFailed : t.syncRequired}
                            </span>
                        </div>
                        {syncStatus === 'success' && syncResult && (
                            <p className="notice notice--ok" role="status">
                                <CheckCircle size={18} aria-hidden="true" />
                                <span>{t.syncSuccessDetail.replace('{action}', syncResult.action || 'updated')}</span>
                            </p>
                        )}
                        {syncStatus === 'failed' && syncError && (
                            <p className="notice notice--error" role="alert">
                                <WarningCircle size={18} aria-hidden="true" />
                                <span>{syncError}</span>
                            </p>
                        )}
                        {syncLogs.length > 0 && (
                            <div role="log" aria-label={t.syncLogsLabel}>
                                <BuildLogs logs={syncLogs.join('\n')} className="log--compact" />
                            </div>
                        )}
                    </section>

                    {activeBuildId && (
                        <p className="notice notice--warn" role="alert">{t.buildAlreadyRunning} <Link to={`/builder/${encodeURIComponent(activeBuildId)}`}>{t.openRunningBuild}</Link></p>
                    )}

                    {!session_id && (
                        <section className="card stack" aria-labelledby="release-setup-heading">
                            <div>
                                <p className="page-head__eyebrow">{t.configuration}</p>
                                <h2 id="release-setup-heading">{t.releaseSetup}</h2>
                            </div>
                            <SearchableFlavorSelect
                                id="build-flavor"
                                label={t.applicationName}
                                placeholder={t.findFlavorForBuild}
                                loading={isLoadingFlavors}
                                disabled={buildButtonDisabled || isLoadingFlavors}
                                selectedValue={selectedFlavor?.flavorName}
                                noResultsText={t.noMatchingFlavor}
                                onSelect={(option) => {
                                    setSelectedFlavor(option?.data);
                                    setVersionCode(option?.data?.versionCode || '');
                                    setVersionName(option?.data?.versionName || '');
                                }}
                                options={options}
                            />
                            {flavorError && <p className="error-text" role="alert">{flavorError}</p>}
                            {!requestedFlavor && !flavorError && !isLoadingFlavors && <p className="hint">{t.noFlavorQuery}</p>}
                            <div className="form-grid">
                                <div className="field">
                                    <label className="label" htmlFor="build-version-code">{t.versionCode}</label>
                                    <input id="build-version-code" className="input" type="number" min="1" onChange={handleVersionCodeChange} value={versionCode} placeholder="Code" />
                                </div>
                                <div className="field">
                                    <label className="label" htmlFor="build-version-name">{t.versionName}</label>
                                    <input id="build-version-name" className="input" type="text" onChange={handleVersionNameChange} value={versionName} placeholder="Name" />
                                </div>
                            </div>
                            <div className="btn-row">
                                <button className="btn btn--primary" type="button" disabled={buildButtonDisabled || buildFormInvalid} onClick={handleSubmit}>
                                    {buildButtonDisabled ? t.running : t.startBuild}
                                </button>
                            </div>
                        </section>
                    )}

                    <details className="card">
                        <summary>{t.openExistingSession}</summary>
                        <div className="stack">
                            <div className="field">
                                <label className="label" htmlFor="build-session-id">{t.sessionId}</label>
                                <input id="build-session-id" className="input" type="text" value={sessionInput} onChange={(event) => setSessionInput(event.target.value)} placeholder={t.enterSessionId} />
                            </div>
                            <div className="btn-row">
                                <button className="btn" type="button" onClick={openSession}>{t.viewLog}</button>
                                <button className="btn btn--danger" type="button" onClick={leaveSession}>{t.resetLog}</button>
                            </div>
                        </div>
                    </details>
                </div>

                <section className="card stack" aria-labelledby="output-heading">
                    <div className="card__head">
                        <div>
                            <p className="page-head__eyebrow">{t.liveOutput}</p>
                            <h2 id="output-heading">{t.buildStream}</h2>
                        </div>
                        <span className={`chip ${buildChip}`}>{buildStatus === 'running' ? t.running : buildStatus === 'success' ? t.buildSucceeded : buildStatus === 'failed' ? t.buildFailed : t.idle}</span>
                    </div>
                    <div aria-live="polite"><BuildLogs logs={logs} /></div>
                    {buildStatus === 'success' && selectedFlavor && <p className="notice notice--ok"><span>{t.buildSucceeded}</span><Link to={`/downloads?q=${encodeURIComponent(selectedFlavor.flavorName)}`}>{t.viewDownloads}</Link></p>}
                    {buildStatus === 'failed' && <p className="notice notice--error"><span>{t.buildFailed}</span><button className="btn btn--pill" type="button" onClick={leaveSession}>{t.resetLog}</button></p>}
                    {logsError && <p className="notice notice--error" role="alert">{logsError}</p>}
                </section>
            </div>
            <Toaster position="top-right" />
        </LifecycleShell>
    );
}

export default BuildPage;
